import { Info } from '@gds/Icons/Paths/Info';
import { YoutubeLight } from '@gds/Icons/Paths/YoutubeLight';
import { Typography } from '@gds/Typography/Typography';
import { useModal } from '@modal/Hooks/UseModal';
import { useAtomValue } from 'jotai';
import Link from 'next/link';
import { currentProductAtom } from '../../../Atoms/CurrentProduct.atom';
import { FieldGroup } from '../../../Elements/FieldGroup/FieldGroup';
import { FieldDescriptor } from '../../../Entities/FinCalcFields.entity';
import { CalculatorMessages } from '../../FinancingCalculator/Calculator.messages';

import { localeAtom } from '@core/Atoms/Locale/Locale.atom';
import { getFormattedCurrency } from '@core/Utils/Intl/GetFormattedCurrency';
import { calculationAtom } from '../../../Atoms/Calculation.atom';
import { isCgi, isPfs, isRci } from '../Helpers/FinanceMap';
import { LegalDisclaimers } from '../LegalDisclaimers/LegalDisclaimers';
import { SpecialServiceOptIn } from '../SpecialService/SpecialServiceOptin';
import styles from './AdditionalServices.module.css';

interface ServiceInfoProps {
  messages: CalculatorMessages;
  service: any;
}

const ServiceInfo = ({ messages, service }: ServiceInfoProps) => {
  const product = useAtomValue(currentProductAtom);
  const additionalServices = product?.additionalServices || [];
  const { openModal } = useModal();
  const calculation = useAtomValue(calculationAtom)!;
  const captiveId = calculation?.captiveId;
  const isCurrentCgi = isCgi(captiveId);
  const locale = useAtomValue(localeAtom);
  const isCgiFinanceEligible = isCurrentCgi && locale === 'fr';

  const openServiceModal = (header: string, content: string) => {
    openModal(<ModalContent content={content} />, {
      header,
      modalClassName: styles.modalContainer,
      modalBodyClassName: styles.modalBody,
    });
  };

  const ModalContent = ({ content }: { content: string }) => (
    <Typography variant="body3" Component="p" className={styles.description}>
      <div dangerouslySetInnerHTML={{ __html: content || '' }} />
    </Typography>
  );

  const getCgiInfoContent = (additionalServiceKey: string) => {
    const service = additionalServices.find(
      service => service.additionalServiceKey === additionalServiceKey,
    );
    if (!service) return { label: '', content: '', serviceMonthlyRate: '' };

    const additionalInsurance = service.serviceDescription || '';
    const additionalPdf = service.pdfFile || '';
    const label = service.label || '';
    const serviceMonthlyRate = service.serviceMonthlyRate || '';

    const pdfLink = messages.pdfAncorLink;
    const content = additionalPdf
      ? `${additionalInsurance}  <a href="${additionalPdf}" target="_blank">${pdfLink}</a>`
      : additionalInsurance;

    return { label, content, serviceMonthlyRate };
  };

  const { label, content, serviceMonthlyRate } = getCgiInfoContent(service.additionalServiceKey);

  return (
    <div key={service.additionalServiceKey} className={styles.serviceContainer}>
      {isCgiFinanceEligible && (
        <>
          <Typography variant="body4" className={styles.servicePrice}>
            <span className={styles.monthlyRate}>
              {getFormattedCurrency(serviceMonthlyRate, locale)}
            </span>
            {messages.perMonth}
          </Typography>

          {service.videoLink && (
            <Link className={styles.videoIcon} href={service.videoLink} target="_blank">
              <YoutubeLight fontSize={20} />
            </Link>
          )}
        </>
      )}

      <span
        className={`${service.videoLink ? styles.infoIcon : styles.infoIconNoVideo} ${
          content === '' ? styles.noInfo : ''
        }`}
      >
        <Info
          onClick={() => content !== '' && openServiceModal(label, content)}
          fontSize={20}
          color={content === '' ? 'transparent' : 'inherit'}
        />
      </span>
    </div>
  );
};

interface AdditionalServicesProps {
  strings: CalculatorMessages;
}

export const AdditionalServices = ({ strings }: AdditionalServicesProps) => {
  const product = useAtomValue(currentProductAtom);
  const additionalServices = product?.additionalServices || [];
  const secondaryDisclaimer = product?.disclaimers?.secondaryCalculationDisclaimer || '';
  const calculation = useAtomValue(calculationAtom)!;
  const captiveId = calculation?.captiveId;
  const isCurrentRci = isRci(captiveId);
  const selectedProduct = calculation?.selectedProduct;
  const isCurrentPorsche = isPfs(captiveId) && selectedProduct === 'privateLeasing';

  if (additionalServices.length === 0) return null;

  return (
    <div className={styles.additionalServicesWrapper}>
      {isCurrentPorsche && <SpecialServiceOptIn strings={strings} />}
      {isCurrentRci && (
        <Typography variant="body1">
          {strings.additionalServices}
          {isCurrentRci && secondaryDisclaimer && (
            <span className={styles.infoIcon}>
              <LegalDisclaimers header={strings.additionalServices} content={secondaryDisclaimer} />
            </span>
          )}
        </Typography>
      )}
      <Typography variant="body2">
        <FieldGroup useDynamicGrid fields={fieldsOptions} />
      </Typography>
      {isCurrentPorsche && (
        <div className={styles.additionalServicesDe}>
          <Typography variant="body4" Component="p">
            {strings.additionalContract}
          </Typography>
        </div>
      )}
    </div>
  );
};

const fieldsOptions: FieldDescriptor[] = [
  {
    id: 'additionalServices',
    dataTestId: 'additionalServices',
    type: 'checkbox',
    isActive: () => true,
    checkboxOptions: ({ messages, product }) => {
      if (!product?.additionalServices?.length) return [];

      return product?.additionalServices.map(service => ({
        id: service.additionalServiceKey,
        label: service.label,
        videoLink: service.videoLink,
        additionalServiceKey: service.additionalServiceKey,
        icon: <ServiceInfo messages={messages} service={service} />,
      }));
    },
    isValid: ({ value }) => value,
    width: () => 'full',
  },
];
